import {FetchBaseQueryMeta, QueryReturnValue} from '@reduxjs/toolkit/query';
import {FetchBaseQueryError} from '@reduxjs/toolkit/dist/query/react';
import {getRequestId} from 'core/modules/request-id';

export const LOGGER_TYPES = {
    PLAIN_TEXT_CONSOLE: 'PLAIN_TEXT_CONSOLE',
    JSON_CONSOLE: 'JSON_CONSOLE',
};

export type TLoggerType = keyof typeof LOGGER_TYPES;

type TRequestType = 'SSR' | 'CLIENT';

type TJSONLog = {
    method: string | null;
    url: string | null;
    status: number | null;
    duration: number;
    durationUnits: string;
    type: TRequestType;
    page: string;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    request_id: string | null;
};

export class Logger<LoggerType extends TLoggerType> {
    private readonly startTime: number;
    private readonly type: keyof typeof LOGGER_TYPES;
    private readonly requestType: TRequestType;
    private duration = 0;
    private durationUnits = 'ms';
    consumerCb?: (log: LoggerType extends 'JSON_CONSOLE' ? TJSONLog : string) => void;

    constructor(
        loggerType: LoggerType,
        requestType: TRequestType,
        cb?: (log: LoggerType extends 'JSON_CONSOLE' ? TJSONLog : string) => void,
    ) {
        this.startTime = performance.now();
        this.type = loggerType;
        this.requestType = requestType;
        this.consumerCb = cb;
    }

    log(query: QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>, page: string) {
        this.duration = Math.round(performance.now() - this.startTime);

        if ('undefined' !== typeof window && 'SSR' === this.requestType) {
            return;
        }

        const log = this.createLog<LoggerType>(query, page);
        if (this.consumerCb) {
            this.consumerCb(log);
        } else {
            this.sendLog(log);
        }
    }

    private createLog<T extends TLoggerType>(
        query: QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>,
        page: string,
    ): T extends 'JSON_CONSOLE' ? TJSONLog : string {
        const requestId = getRequestId();

        if (this.type === LOGGER_TYPES.JSON_CONSOLE) {
            return {
                method: query?.meta?.request?.method || null,
                url: query?.meta?.request?.url || null,
                status: query?.meta?.response?.status || null,
                duration: this.duration,
                durationUnits: this.durationUnits,
                type: this.requestType,
                page,
                // eslint-disable-next-line @typescript-eslint/naming-convention
                request_id: requestId,
            } as any;
        }
        return `[${this.requestType}] ${query?.meta?.request?.url} :: [${query?.meta?.request?.method}] ${query?.meta?.response?.status} :: ${this.duration}${this.durationUnits} :: from: ${page} ${requestId ? `:: request_id: ${requestId}` : ''}` as any;
    }

    // Метод можно расширить, если захотим например писать в файл
    private sendLog<T extends TLoggerType>(log: T extends 'JSON_CONSOLE' ? TJSONLog : string) {
        if (this.type === LOGGER_TYPES.JSON_CONSOLE) {
            console.info(JSON.stringify(log));
        }
        if (this.type === LOGGER_TYPES.PLAIN_TEXT_CONSOLE) {
            console.info(log);
        }
    }
}
